import type { Doctor, Letterhead } from '../api'

interface Props {
  doctor: Doctor
  letterhead: Letterhead | null
}

const A4_HEIGHT_MM = 297
const A4_WIDTH_MM = 210

/**
 * The calibration sheet (blueprint 10). Printed onto one piece of the clinic's
 * own letterhead, it shows exactly where each zone will land and carries a
 * millimetre ruler down two edges, so the margins can be read off the paper
 * and typed back in. The alternative is a dozen wasted sheets and guesswork.
 */
export default function CalibrationSheet({ doctor, letterhead }: Props) {
  const mode = letterhead?.mode ?? 'full'
  const preprinted = mode === 'preprinted'
  const top = Number(letterhead?.marginTopMm ?? (preprinted ? 45 : 12))
  const bottom = Number(letterhead?.marginBottomMm ?? (preprinted ? 30 : 12))
  const left = Number(letterhead?.marginLeftMm ?? 15)
  const right = Number(letterhead?.marginRightMm ?? 15)

  const vTicks = Array.from({ length: Math.floor(A4_HEIGHT_MM / 5) + 1 }, (_, i) => i * 5)
  const hTicks = Array.from({ length: Math.floor(A4_WIDTH_MM / 5) + 1 }, (_, i) => i * 5)

  return (
    <div className="cal-print">
      <style>{'@media print { @page { size: A4 portrait; margin: 0; } }'}</style>

      <div className="cal-sheet">
        <div className="cal-ruler cal-ruler-v">
          {vTicks.map((mm) => (
            <div
              key={mm}
              className={`cal-tick${mm % 10 === 0 ? ' major' : ''}`}
              style={{ top: `${mm}mm` }}
            >
              {mm % 10 === 0 && <span className="cal-num">{mm}</span>}
            </div>
          ))}
        </div>

        <div className="cal-ruler cal-ruler-h">
          {hTicks.map((mm) => (
            <div
              key={mm}
              className={`cal-tick-h${mm % 10 === 0 ? ' major' : ''}`}
              style={{ left: `${mm}mm` }}
            >
              {mm % 10 === 0 && <span className="cal-num">{mm}</span>}
            </div>
          ))}
        </div>

        <div
          className="cal-area"
          style={{
            top: `${top}mm`,
            bottom: `${bottom}mm`,
            left: `${left}mm`,
            right: `${right}mm`,
          }}
        >
          <div className="cal-legend">
            <strong>Calibration sheet</strong> — {doctor.fullName}
            <div>
              Mode: {preprinted ? 'Pre-printed stationery (body only)' : 'Full print on blank A4'}
            </div>
            <div>Margins: top {top} · bottom {bottom} · left {left} · right {right} (mm)</div>
            <div className="cal-hint">
              Print this on one sheet of the clinic&rsquo;s letterhead. Everything inside the
              dashed box is where the prescription will print. If it overlaps the pre-printed
              header, read the overlap off the ruler and raise the top margin by that many
              millimetres.
            </div>
          </div>

          {!preprinted && <div className="cal-zone">1 · Clinic and doctor header</div>}
          <div className="cal-zone">2 · Patient strip</div>
          <div className="cal-zone">3 · Vitals strip</div>
          <div className="cal-zone cal-grow">4 · Clinical body — complaints, findings, diagnosis, ℞, tests, advice</div>
          <div className="cal-zone">5 · Signature and footer</div>
        </div>
      </div>
    </div>
  )
}
